import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';

// Utils
import { useAsyncAction } from './hooks';
import { accountApi } from '../api';
import { AccountResetPasswordRequest } from '../models/request';
import { BaseResponse, KeyValue } from '../models/common';
import { getCultureBrowser, isNullUndefinedEmpty } from '../services/utils';
import {
  STORAGE_REFRESH_TOKEN,
  STORAGE_TOKEN,
  saveStringStorage
} from '../services/storage';

// Redux
import { userSetCurrent } from '../store/user';
import { store } from '../store';
import { UserTypeEnum } from '../services/enum/UserTypeEnum';

interface ILoginFormOptions {
  onSuccess?: () => void;
  redirectRoute?: string | null;
}

export interface IChangePassowordForm {
  newPassword: string;
  confirmPassword: string;
  token: string | null;
  userId: string | null;
}

export function useResetPassword(options: ILoginFormOptions = {}) {
  const cultureBrowser = getCultureBrowser();
  const navigate = useNavigate();
  const { onSuccess, redirectRoute } = options;

  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [serverErrors, setServerErrors] = useState<string[] | null>(null);
  const methods = useForm<IChangePassowordForm>({
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all'
  });
  const { handleSubmit } = methods;

  const newPassword = methods.watch('newPassword');
  const confirmPassword = methods.watch('confirmPassword');

  useEffect(() => {
    setServerErrors(null);
  }, [newPassword, confirmPassword]);

  useEffect(() => {
    if (confirmPassword && confirmPassword.length > 0) {
      methods.trigger('confirmPassword');
    }
  }, [newPassword]);

  const [submit, submitInProgress] = useAsyncAction(
    (data: IChangePassowordForm) => {
      setServerErrors(null);
      const request: AccountResetPasswordRequest = {
        userId: data.userId || '',
        token: data.token || '',
        newPassword: data.newPassword,
        idCultureBrowser: cultureBrowser
      };

      return accountApi
        .accountResetPasswordApi(request)
        .then(response => {
          const { result } = response.data;
          setIsSuccess(true);
          if(result.idUserType == UserTypeEnum.UI){
            store.dispatch(userSetCurrent(result));
            saveStringStorage(STORAGE_TOKEN, result.token);
            saveStringStorage(STORAGE_REFRESH_TOKEN, result.refreshToken);

            // Navigate route
            let redirectRouteValue: string = '';
            if (!isNullUndefinedEmpty(redirectRoute)) {
              redirectRouteValue = String(redirectRoute);
            } else {
              redirectRouteValue = result.redirectRoute;
            }
            if (!isNullUndefinedEmpty(redirectRouteValue)) {
              navigate(redirectRouteValue);
            }
          }

        })
        .catch((error: AxiosError<BaseResponse>) => {
          if (error.response && error.response.data) {
            const baseResponseError = error.response.data as BaseResponse;
            if (
              baseResponseError.errorMessages &&
              baseResponseError.errorMessages.length > 0
            ) {
              setServerErrors(
                baseResponseError.errorMessages.map((x: KeyValue) => x.value)
              );
            }
          }
        });
    },
    [accountApi.accountResetPasswordApi, setServerErrors, onSuccess]
  );

  return {
    submit: useMemo(() => handleSubmit(submit), [handleSubmit, submit]),
    submitInProgress: submitInProgress || methods.formState.isSubmitting,
    serverErrors,
    errors: methods.formState.errors,
    register: methods.register,
    watch: methods.watch,
    setValue: methods.setValue,
    getValues: methods.getValues,
    isSuccess
  };
}
